import React, {useCallback} from 'react'
import {ConfirmCardSetupData, SetupIntent} from '@stripe/stripe-js'
import CreditCardForm, {CreditCardFormResult} from 'lib/stripe/CreditCardForm'
import {Loader} from 'obvio/Billing/CreditCardSection/DefaultCardManagement/NewCardForm'
import {api} from 'lib/url'
import {teamMemberClient} from 'obvio/obvio-client'
import {useAsync} from 'lib/async'
import {useOptionalBillingTransaction} from 'obvio/Billing/BillingTransactionProvider'

export type SaveCreditCardFormProps = {
  onSuccess: (paymentMethodId: string) => void | Promise<void>
  submitLabel: string
  className?: string
  disabled?: boolean
}

export default function SaveCreditCardForm(props: SaveCreditCardFormProps) {
  const {onSuccess} = props
  const setupIntent = useSetupIntent()
  const recordIntentError = useRecordIntentError()
  const billingTransaction = useOptionalBillingTransaction()

  const clientSecret = setupIntent?.client_secret

  if (!clientSecret) {
    return <Loader />
  }

  const onSubmit = async (result: CreditCardFormResult) => {
    const submit = async () => {
      const {cardElement, name, stripe} = result
      const data: ConfirmCardSetupData = {
        payment_method: {
          card: cardElement,
          billing_details: {
            name,
          },
        },
      }

      const {setupIntent: updatedIntent, error} = await stripe.confirmCardSetup(
        clientSecret,
        data,
      )

      if (error) {
        const message = error.message || 'Payment Error'
        recordIntentError(error)
        throw new Error(message)
      }

      if (!updatedIntent?.payment_method) {
        throw new Error(
          'Failed to add payment method, you will not be charged. Please contact support.',
        )
      }

      const paymentMethodId =
        typeof updatedIntent.payment_method === 'string'
          ? updatedIntent.payment_method
          : updatedIntent.payment_method.id

      await Promise.resolve(onSuccess(paymentMethodId))
    }

    if (billingTransaction) {
      await billingTransaction.runTransaction(submit)
      return
    }

    await submit()
  }

  return <CreditCardForm {...props} onSubmit={onSubmit} />
}

function useSetupIntent() {
  const request = useCallback(() => {
    const url = api('/stripe/setup_intent')

    return teamMemberClient.get<SetupIntent>(url)
  }, [])

  const {data} = useAsync(request)

  return data
}

export function useRecordIntentError() {
  return useCallback((error) => {
    const url = api('/stripe/payment_intent_error')

    return teamMemberClient.post<void>(url, error)
  }, [])
}
